import React, { useState, useEffect } from 'react';
import './DynamicOtherNamesTable.css';

interface OtherNameEntry {
  lastName: string;
  givenName: string;
}

interface DynamicOtherNamesTableProps {
  value: { [key: string]: OtherNameEntry };
  onChange: (entries: { [key: string]: OtherNameEntry }) => void;
  required?: boolean;
}

const DynamicOtherNamesTable: React.FC<DynamicOtherNamesTableProps> = ({
  value,
  onChange,
  required = false
}) => {
  const [entries, setEntries] = useState<{ [key: string]: OtherNameEntry }>(value || {});

  useEffect(() => {
    setEntries(value || {});
  }, [value]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        addEntry();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const addEntry = () => {
    const entryCount = Object.keys(entries).length;
    const newKey = `otherName${entryCount + 1}`;
    
    const newEntry: OtherNameEntry = {
      lastName: '',
      givenName: ''
    };
    
    const updatedEntries = { ...entries, [newKey]: newEntry };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const removeEntry = (key: string) => {
    const { [key]: removed, ...remainingEntries } = entries;
    
    // Renumber the remaining entries
    const renumberedEntries: { [key: string]: OtherNameEntry } = {};
    const remainingKeys = Object.keys(remainingEntries).sort();
    
    remainingKeys.forEach((oldKey, index) => {
      const newKey = `otherName${index + 1}`;
      renumberedEntries[newKey] = remainingEntries[oldKey];
    });
    
    setEntries(renumberedEntries);
    onChange(renumberedEntries);
  };

  const updateEntry = (key: string, field: keyof OtherNameEntry, value: string) => {
    const updatedEntries = {
      ...entries,
      [key]: { ...entries[key], [field]: value }
    };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  // Initialize with at least one entry if empty
  useEffect(() => {
    if (Object.keys(entries).length === 0) {
      addEntry();
    }
  }, []);

  const entryKeys = Object.keys(entries).sort();

  return (
    <div className="dynamic-other-names-table">
      <div className="table-header">
        <h4>Other Names Info</h4>
        <button
          type="button"
          className="add-btn"
          onClick={addEntry}
          title="Add other name entry (Ctrl+Enter)"
        >
          + Add Other Name Entry
        </button>
      </div>

      {entryKeys.length === 0 ? (
        <div className="empty-state">
          <p>No other names entered yet.</p>
          <button
            type="button"
            className="add-btn"
            onClick={addEntry}
          >
            + Add Other Name Entry
          </button>
        </div>
      ) : (
        <div className="entries-container">
          {entryKeys.map((key, index) => (
            <div key={key} className="entry-card">
              <div className="entry-header">
                <div className="entry-number">
                  <span className="entry-badge">{index + 1}</span>
                  <h5>Entry {index + 1}</h5>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeEntry(key)}
                  title="Remove this other name entry"
                >
                  ×
                </button>
              </div>
              
              <div className="entry-form">
                <div className="form-row form-row-1">
                  <div className="field-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={entries[key].lastName}
                      onChange={(e) => updateEntry(key, 'lastName', e.target.value)}
                      placeholder="e.g., Smith"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>Given Name</label>
                    <input
                      type="text"
                      value={entries[key].givenName}
                      onChange={(e) => updateEntry(key, 'givenName', e.target.value)}
                      placeholder="e.g., John"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {entryKeys.length > 0 && (
        <div className="table-footer">
          <button
            type="button"
            className="add-btn"
            onClick={addEntry}
            title="Add another other name entry (Ctrl+Enter)"
          >
            + Add Another Other Name Entry
          </button>
        </div>
      )}

      {required && entryKeys.length === 0 && (
        <small className="error-text">At least one other name entry is required</small>
      )}
    </div>
  );
};

export default DynamicOtherNamesTable; 